import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
	patternsBehavioral,
	patternsCreational,
	patternsStructural,
} from '../../constants/patterns';

const patternGroups = [
	{ categoryLabel: 'Порождающий паттерн', items: patternsCreational },
	{ categoryLabel: 'Структурный паттерн', items: patternsStructural },
	{ categoryLabel: 'Поведенческий паттерн', items: patternsBehavioral },
];

const patternPageByUrl = patternGroups.flatMap(({ categoryLabel, items }) => (
	items.map((pattern) => ({
		...pattern,
		categoryLabel,
	}))
));

const PatternReadingBar = () => {
	const { pathname } = useLocation();
	const currentPattern = useMemo(
		() => patternPageByUrl.find((pattern) => pattern.url === pathname),
		[pathname]
	);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		if (!currentPattern) {
			setIsVisible(false);
			return undefined;
		}

		let animationFrameId = null;

		const updateReadingState = () => {
			animationFrameId = null;
			const heroTitle = document.querySelector('main h1');
			const titleBottom = heroTitle ? heroTitle.getBoundingClientRect().bottom : 0;

			setIsVisible(window.scrollY > 80 && titleBottom <= 72);
		};

		const scheduleUpdate = () => {
			if (animationFrameId !== null) {
				return;
			}

			animationFrameId = window.requestAnimationFrame(updateReadingState);
		};

		updateReadingState();
		window.addEventListener('scroll', scheduleUpdate, { passive: true });
		window.addEventListener('resize', scheduleUpdate);

		return () => {
			if (animationFrameId !== null) {
				window.cancelAnimationFrame(animationFrameId);
			}
			window.removeEventListener('scroll', scheduleUpdate);
			window.removeEventListener('resize', scheduleUpdate);
		};
	}, [currentPattern]);

	if (!currentPattern) {
		return null;
	}

	return (
		<Box
			component="aside"
			aria-label="Текущий паттерн"
			sx={{
				position: 'fixed',
				top: { xs: 64, md: 72 },
				left: 0,
				right: 0,
				zIndex: 1090,
				pointerEvents: 'none',
				transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
				opacity: isVisible ? 1 : 0,
				transition: 'transform 180ms ease, opacity 180ms ease',
			}}
		>
			<Box
				sx={{
					bgcolor: 'rgba(255, 255, 255, 0.96)',
					backdropFilter: 'blur(12px)',
					borderBottom: '1px solid rgba(var(--gof-rgb-primary), 0.08)',
					boxShadow: '0 8px 24px rgba(var(--gof-rgb-primary), 0.08)',
					'body[data-theme="dark"] &': {
						bgcolor: 'rgba(21, 53, 77, 0.92)',
						borderBottom: '1px solid rgba(83, 139, 179, 0.28)',
						boxShadow: '0 8px 24px rgba(1, 39, 66, 0.28)',
					},
				}}
			>
				<Box
					sx={{
						maxWidth: 'lg',
						mx: 'auto',
						px: { xs: 2, sm: 3 },
						py: { xs: 0.9, md: 1 },
						display: 'flex',
						alignItems: { xs: 'flex-start', sm: 'center' },
						justifyContent: 'space-between',
						gap: 1.5,
						flexDirection: { xs: 'column', sm: 'row' },
					}}
				>
					<Typography
						component="span"
						sx={{
							color: 'var(--gof-color-muted)',
							fontSize: { xs: '0.72rem', md: '0.75rem' },
							fontWeight: 700,
							lineHeight: 1.2,
							textTransform: 'uppercase',
							'body[data-theme="dark"] &': {
								color: '#C7D9E6',
							},
						}}
					>
						{currentPattern.categoryLabel}
					</Typography>
					<Typography
						component="span"
						sx={{
							color: 'var(--gof-color-primary)',
							fontSize: { xs: '0.98rem', md: '1.05rem' },
							fontWeight: 800,
							lineHeight: 1.2,
							overflowWrap: 'anywhere',
							textAlign: { xs: 'left', sm: 'right' },
							'body[data-theme="dark"] &': {
								color: '#F2F7FB',
							},
						}}
					>
						{currentPattern.fullName}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};

export default PatternReadingBar;
